<script lang="ts">
  // The Garmin Connect Sync page (/garmin-health/sync, GarminHealthController@getIndex): connect or
  // disconnect Garmin Connect, choose the activity types that sync and whether scheduled workouts
  // are sent to Garmin, and ask Garmin for past activities.
  import { Button } from '$lib/components/ui/button'
  import * as Card from '$lib/components/ui/card'
  import SettingSwitch from '@/Common/SettingSwitch.svelte'
  import ActivityTypes from '@/Integration/Sync/ActivityTypes.svelte'
  import ActivityTypesDialog from '@/Integration/Sync/ActivityTypesDialog.svelte'
  import ConnectButton from '@/Integration/Sync/ConnectButton.svelte'
  import SyncConnection from '@/Integration/Sync/SyncConnection.svelte'
  import SyncHelp from '@/Integration/Sync/SyncHelp.svelte'
  import SyncHelpItem from '@/Integration/Sync/SyncHelpItem.svelte'
  import { saveGarminSyncSettings, type BasicResponse } from '@/Util/api'
  import { ErrorCode } from '@/Util/errorCodes'
  import { notifyApiError } from '@/Util/notify'
  import GarminImport from './GarminImport.svelte'
  import HistoricalDataDialog from './HistoricalDataDialog.svelte'

  export let connected: boolean
  /** Linked through the old Garmin Connect integration only, which has to be authorized again. */
  export let legacy = false
  export let sendWorkouts: boolean
  /** The sport's own types, always synced. */
  export let defaultTypes: string[]
  /** Other types the athlete can add. */
  export let typeOptions: { value: string, checked: boolean }[]
  export let expectedMinutes: number | null = null
  export let pendingSince: number | null = null
  export let redirectUrl: string
  export let disconnectUrl: string
  export let settingsUrl: string
  export let backfillUrl: string

  $: chosenTypes = typeOptions.filter((t) => t.checked).map((t) => t.value)
  let typesOpen = false
  let historicalOpen = false

  function saved(res: BasicResponse, message: string) {
    if (res.success) return true
    notifyApiError(res, { code: ErrorCode.SYNC_SETTING_FAILED, message })
    return false
  }

  const saveSendWorkouts = async (on: boolean) =>
    saved(await saveGarminSyncSettings(settingsUrl, { send_workouts: on }), 'Could not save your workout setting. Please try again.')

  // The server keeps the types it has when it gets none. A type that always syncs stands for "no
  // more": sending one clears the others, and it syncs anyway.
  const saveTypes = async (types: string[]) =>
    saved(await saveGarminSyncSettings(settingsUrl, { filter: types.length ? types : defaultTypes.slice(0, 1) }),
      'Could not save your activity types. Please try again.')
</script>

<div class="mx-auto flex max-w-[1100px] flex-col gap-4 px-4 pb-8">
  <SyncConnection service="garmin" {connected} {disconnectUrl}
    title={legacy ? 'Update your Garmin Connect link' : undefined}
    benefits={[
      'New activities uploaded to Garmin Connect arrive in Xert automatically',
      'Choose which activity types Xert receives, and import your past activities',
      'Send your scheduled workouts to your Garmin device',
    ]}
    warning={legacy ? 'Your activities still sync through the old Garmin Connect link. Authorize Xert again to choose activity types and import past activities.' : ''}
    disconnectMessage="Xert stops receiving your Garmin Connect activities and can no longer send workouts to your Garmin device. Activities already in Xert stay.">
    <svelte:fragment slot="actions">
      <Button variant="outline" size="sm" on:click={() => (historicalOpen = true)}>
        <i class="fa-solid fa-rotate mr-2" aria-hidden="true" /> Reconnect
      </Button>
    </svelte:fragment>
    <ConnectButton slot="connect" service="garmin" label={legacy ? 'Authorize Garmin again' : undefined} on:click={() => (historicalOpen = true)} />
  </SyncConnection>

  {#if connected}
    <div class="grid gap-4 lg:grid-cols-2">
      <Card.Root>
        <Card.Header class="pb-0">
          <Card.Title class="text-base">Automatic sync</Card.Title>
          <Card.Description>New activities sync as soon as they're uploaded to Garmin Connect.</Card.Description>
        </Card.Header>
        <Card.Content class="divide-y divide-border">
          <ActivityTypes {defaultTypes} {chosenTypes} on:click={() => (typesOpen = true)} />
          <SettingSwitch id="garmin-send-workouts" bind:checked={sendWorkouts} save={saveSendWorkouts}
            label="Send scheduled workouts to Garmin"
            description="Compatible workouts scheduled in Xert are sent to your Garmin Connect account." />
        </Card.Content>
      </Card.Root>

      <GarminImport {backfillUrl} {expectedMinutes} {pendingSince} on:historicalData={() => (historicalOpen = true)} />
    </div>
  {/if}

  <SyncHelp>
    <SyncHelpItem title="Syncing problems?">Reconnect Garmin Connect and leave every permission Garmin asks for switched on.</SyncHelpItem>
    <SyncHelpItem title="Past activities not arriving?">
      Garmin only sends them with <em>Historical Data</em> switched on for Xert.
      <button type="button" class="cursor-pointer border-0 bg-transparent p-0 text-primary underline-offset-2 hover:underline" on:click={() => (historicalOpen = true)}>Show me how</button>.
    </SyncHelpItem>
    <SyncHelpItem title="Activities from elsewhere?">Upload activity files on the <a href="/activities/upload">Upload Activity page</a>, or connect <a href="/strava">Strava</a>.</SyncHelpItem>
  </SyncHelp>
</div>

{#if connected}
  <ActivityTypesDialog id="garmin" serviceName="Garmin Connect" bind:open={typesOpen} {defaultTypes} bind:typeOptions save={saveTypes} />
{/if}

<HistoricalDataDialog bind:open={historicalOpen} redirect={redirectUrl}
  cta={connected ? 'Reconnect with Garmin' : legacy ? 'Authorize Garmin again' : 'Continue to Garmin'} />
